/**
 * RecurringBuilder.
 *
 * The drawer shell for the Recurring Builder. Owns the layout (sticky
 * header, scrollable body, sticky footer, dim backdrop) and the open
 * animation. Composes nothing itself; the parent passes the 4 section
 * components as children so each section stays in its own file.
 *
 * Desktop: 560px right-side drawer with a backdrop over the page.
 * Mobile:  full-screen takeover (inset: 0).
 *
 * Closes on backdrop click or Escape, routed through onClose so the
 * parent's useRecurringBuilder can show the discard-confirm when dirty.
 *
 * Also exports BuilderSection, the shared numbered-section wrapper
 * each section file uses so numbering, dividers and spacing stay
 * consistent across sections.
 */

import {
  useEffect,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { X, Trash2 } from "lucide-react";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import type { RecurringTemplate } from "../recurringTypes";
import type { BuilderMode } from "./builderTypes";

export interface RecurringBuilderProps {
  isOpen: boolean;
  mode: BuilderMode;
  isValid: boolean;
  isSaving: boolean;
  editingTemplate: RecurringTemplate | null;
  onClose: () => void;
  onSubmit: () => void;
  onDelete?: () => void;
  children: ReactNode;
}

export function RecurringBuilder({
  isOpen,
  mode,
  isValid,
  isSaving,
  editingTemplate,
  onClose,
  onSubmit,
  onDelete,
  children,
}: RecurringBuilderProps) {
  const { isMobile } = useBreakpoint();

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const title = mode === "edit" ? "Edit template" : "New recurring template";
  const submitLabel = mode === "edit" ? "Save changes" : "Save template";
  const showDelete = mode === "edit" && !!onDelete && !!editingTemplate;

  const drawer = (
    <div style={rootStyle}>
      <div style={backdropStyle} onClick={onClose} aria-hidden />
      <div
        style={isMobile ? drawerStyleMobile : drawerStyleDesktop}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div style={headerStyle}>
          <div style={titleStyle}>{title}</div>
          <button
            type="button"
            style={closeBtnStyle}
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div style={bodyStyle}>{children}</div>

        <div style={footerStyle}>
          {showDelete ? (
            <button
              type="button"
              style={deleteBtnStyle}
              onClick={onDelete}
              disabled={isSaving}
            >
              <Trash2 size={13} style={{ marginRight: 6 }} />
              Delete template
            </button>
          ) : (
            <div />
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              style={ghostBtnStyle}
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              style={{
                ...primaryBtnStyle,
                ...(isValid && !isSaving ? {} : primaryBtnDisabledStyle),
              }}
              onClick={onSubmit}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(drawer, document.body);
}

// ---------- Shared section wrapper, exported for use by section files ----------

export interface BuilderSectionProps {
  number: 1 | 2 | 3 | 4;
  title: string;
  children: ReactNode;
}

export function BuilderSection({ number, title, children }: BuilderSectionProps) {
  return (
    <div style={sectionStyle}>
      <div style={sectionLabelStyle}>
        <span style={sectionNumStyle}>{number}</span>
        {title}
      </div>
      {children}
    </div>
  );
}

// ---------- Styles ----------

const rootStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1500,
};

const backdropStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "rgba(14, 26, 26, 0.42)",
};

const drawerStyleDesktop: CSSProperties = {
  position: "absolute",
  top: 0,
  right: 0,
  width: 560,
  maxWidth: "100vw",
  height: "100%",
  background: "#ffffff",
  boxShadow: "-16px 0 32px -8px rgba(14, 26, 26, 0.18)",
  display: "flex",
  flexDirection: "column",
};

const drawerStyleMobile: CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "#ffffff",
  display: "flex",
  flexDirection: "column",
};

const headerStyle: CSSProperties = {
  padding: "20px 24px 16px",
  borderBottom: "1px solid #D8DEE6",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
  background: "#ffffff",
};

const titleStyle: CSSProperties = {
  fontSize: 17,
  fontWeight: 700,
  color: "#0E1A1A",
  letterSpacing: "-0.01em",
};

const closeBtnStyle: CSSProperties = {
  width: 32,
  height: 32,
  border: "none",
  background: "transparent",
  borderRadius: 6,
  cursor: "pointer",
  color: "#3A4A52",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const bodyStyle: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: 0,
  background: "#ffffff",
};

const footerStyle: CSSProperties = {
  padding: "14px 24px",
  borderTop: "1px solid #D8DEE6",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  background: "#ffffff",
  flexShrink: 0,
};

const sectionStyle: CSSProperties = {
  padding: "20px 24px 24px",
  borderBottom: "1px solid #E7EAF0",
};

const sectionLabelStyle: CSSProperties = {
  fontSize: 10,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  fontWeight: 700,
  color: "#3A4A52",
  marginBottom: 14,
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const sectionNumStyle: CSSProperties = {
  display: "inline-flex",
  width: 18,
  height: 18,
  alignItems: "center",
  justifyContent: "center",
  background: "#0E1A1A",
  color: "#ffffff",
  borderRadius: 50,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0,
};

const primaryBtnStyle: CSSProperties = {
  padding: "10px 18px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  border: "1px solid #0E1A1A",
  background: "#0E1A1A",
  color: "#ffffff",
  fontFamily: "inherit",
};

const primaryBtnDisabledStyle: CSSProperties = {
  background: "#6B7684",
  borderColor: "#6B7684",
  cursor: "not-allowed",
  opacity: 0.6,
};

const ghostBtnStyle: CSSProperties = {
  padding: "10px 18px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  border: "1px solid #D8DEE6",
  background: "#ffffff",
  color: "#0E1A1A",
  fontFamily: "inherit",
};

const deleteBtnStyle: CSSProperties = {
  padding: "10px 14px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  border: "1px solid #FDA29B",
  background: "#ffffff",
  color: "#B42318",
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
};

export default RecurringBuilder;